import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, Check, Download, Eye, Mail } from 'lucide-react';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import DocumentEmailWizard from '@/components/documents/DocumentEmailWizard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { downloadDocumentById } from '@/lib/downloadDocumentById';

interface ConductingMeetingPhase1Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

export function ConductingMeetingPhase1({ task, phaseData, onPhaseComplete }: ConductingMeetingPhase1Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<any | null>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);
  const [unaData, setUnaData] = useState<any | null>(null);

  const workflowData = task.workflow_data || {};

  // Required documents for Phase 1 - NONE require signatures
  const requiredDocuments = [
    { 
      key: 'meeting_conductors_guide', 
      title: 'Meeting Conductor\'s Guide',
      requiresSignature: false
    },
    { 
      key: 'meeting_attendance_sheet', 
      title: 'Roll Call & Attendance Sheet',
      requiresSignature: false
    },
  ];

  useEffect(() => {
    loadDocuments();
    loadUnaData();
  }, [task.id]);

  async function loadUnaData() {
    if (!task.una_id) return;
    
    const { data, error } = await supabase
      .from('unas')
      .select(`
        *,
        una_members(
          id,
          user_id,
          full_name,
          email,
          role,
          status
        )
      `)
      .eq('id', task.una_id)
      .maybeSingle();

    if (!error && data) {
      setUnaData(data);
    }
  }

  async function loadDocuments() {
    const { data, error } = await supabase
      .from('generated_documents')
      .select('*')
      .eq('task_id', task.id)
      .order('generated_at', { ascending: true }) as { data: any[] | null; error: any };

    if (error) {
      console.error('Error loading documents:', error);
      return;
    }

    // Filter to phase 1 documents by document_type
    const phase1Docs = (data || []).filter(d => {
      const docType = d.document_type || '';
      return docType && (
        docType.includes('meeting_conductors_guide') ||
        docType.includes('meeting_attendance_sheet')
      );
    });

    setDocuments(phase1Docs);
  }

  async function handleGenerateDocument(docReq: any) {
    // Guard against missing task data
    if (!task?.id || !task?.una_id) {
      toast({
        title: 'Error',
        description: 'Task data not loaded yet. Please wait and try again.',
        variant: 'destructive',
      });
      return;
    }
    
    setGenerating(docReq.key);
    
    try {
      // Call edge function to generate document
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      console.log('Generating document:', {
        templateId: docReq.key,
        taskId: task.id,
        unaId: task.una_id,
      });

      const response = await supabase.functions.invoke('generate-document', {
        body: {
          templateId: docReq.key,
          taskId: task.id,
          unaId: task.una_id,
          workflow_data: workflowData,
        },
      });

      if (response.error) {
        let friendlyMessage = 'Document generation failed';
        try {
          if (response.error.context && typeof response.error.context.json === 'function') {
            const errorBody = await response.error.context.json();
            friendlyMessage = errorBody?.error || friendlyMessage;
          }
        } catch {
          // Fall back to generic message
        }
        throw new Error(friendlyMessage);
      }

      toast({
        title: 'Document Generated',
        description: `${docReq.title} has been created successfully.`,
      });

      await loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate document',
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  }

  async function handleMarkPhaseComplete() {
    // Check at least one document generated
    if (documents.length < 1) {
      toast({
        title: 'Documents Required',
        description: 'Please generate at least one document before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Phase 1 Complete',
      description: 'Meeting preparation documents ready. Proceeding to conduct the meeting.',
    });

    onPhaseComplete();
  }

  function getDocumentStatus(docKey: string) {
    const doc = documents.find(d => d.document_type === docKey);
    if (!doc) return { status: 'not_generated', doc: null };
    
    // Non-signature documents are complete once generated
    return { status: 'complete', doc };
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle>Phase 1: Prepare Meeting Materials</CardTitle>
                <Badge>Current Phase</Badge>
              </div>
              <p className="text-muted-foreground">
                Generate conductor's guide and attendance sheet for the meeting
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Steps to Complete */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Steps to Complete:</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Collect meeting details (date, time, location, attendees)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Generate conductor's guide with procedural notes</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Prepare roll call and attendance sheet</span>
            </li>
          </ul>
          <p className="text-sm text-muted-foreground mt-3 italic">
            Note: Meeting agenda items will be created during the live meeting in Phase 2.
          </p>
        </CardContent>
      </Card>

      {/* Required Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Required Documents:</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {requiredDocuments.map((docReq) => {
              const { status, doc } = getDocumentStatus(docReq.key);

              return (
                <div
                  key={docReq.key}
                  className="border rounded-lg p-4 flex items-start justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <h5 className="font-medium">{docReq.title}</h5>
                    </div>
                    {doc && (
                      <div className="mt-2">
                        <Badge variant="default">
                          Generated
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {!doc ? (
                      <Button
                        onClick={() => handleGenerateDocument(docReq)}
                        disabled={generating === docReq.key}
                      >
                        {generating === docReq.key ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          'Generate'
                        )}
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewDocument(doc)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Preview
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await downloadDocumentById(doc.id, { underscoreFilename: true });
                              
                              toast({
                                title: 'Download Started',
                                description: 'Document is downloading...',
                              });
                            } catch (error) {
                              console.error('Download error:', error);
                              toast({
                                title: 'Download Failed',
                                description: 'Could not download document',
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedDocForEmail(doc.id);
                            setEmailWizardOpen(true);
                          }}
                          className="gap-1"
                        >
                          <Mail className="h-3 w-3" />
                          Email
                        </Button>
                        
                        <DocumentUploadButton
                          documentId={doc.id}
                          documentName={doc.document_name}
                          onUploadComplete={() => loadDocuments()}
                          fileUrl={doc.file_url}
                          hasUploadedFile={doc.file_path?.includes('-uploaded.pdf')}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Complete Phase Button - Always show, but disable when not ready */}
      <Alert className={documents.length < 1 ? 'border-muted' : 'border-green-200 bg-green-50 dark:bg-green-950/20'}>
        <AlertDescription>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {documents.length >= 1 ? 'Documents Ready' : 'Complete Phase 1'}
              </p>
              <p className="text-sm text-muted-foreground">
                {documents.length >= 1 
                  ? 'At least one document prepared. Ready to proceed to conduct the meeting.'
                  : 'Generate at least one document to continue.'}
              </p>
            </div>
            <Button 
              onClick={handleMarkPhaseComplete}
              disabled={documents.length < 1}
            >
              Complete Phase 1
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {/* Modals */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {emailWizardOpen && selectedDocForEmail && (
        <DocumentEmailWizard
          open={emailWizardOpen}
          onOpenChange={setEmailWizardOpen}
          taskId={task.id}
          unaId={task.una_id}
          preSelectedDocumentId={selectedDocForEmail}
        />
      )}
    </div>
  );
}
